import { type Instant, type LocalDate, formatLocalDate, parseInstant, parseLocalDate } from '@taler/domain'
import type { Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, ledgerKey } from '../http/keys.ts'

export type ExportStatus = Schemas['ExportJobResponse']['status']
export type ExportFormat = Schemas['ExportJobResponse']['format']
export type BackupRestoreResult = Schemas['BackupRestoreResult']

export interface ExportJob {
  readonly id: string
  readonly status: ExportStatus
  readonly format: ExportFormat
  readonly createdAt: Instant
  readonly startedAt: Instant | undefined
  readonly completedAt: Instant | undefined
  readonly fromDate: LocalDate
  readonly untilDate: LocalDate
  readonly requestedBy: string
  readonly errorMessage: string | undefined
}

export interface CreateExportInput {
  readonly format: ExportFormat
  readonly fromDate: LocalDate
  readonly untilDate: LocalDate
}

export interface ExportDownload {
  readonly blob: Blob
  readonly filename: string
}

export const exportKeys = {
  list: (ledgerId: string): CacheKey => ledgerKey(ledgerId, 'exports', 'list'),
  /** Everything a new export request makes stale. */
  invalidatedByWrite: (ledgerId: string): Invalidations => [ledgerKey(ledgerId, 'exports')],
} as const

export interface ExportsRepository {
  list(ledgerId: string, signal?: AbortSignal): Promise<readonly ExportJob[]>
  create(ledgerId: string, input: CreateExportInput): Promise<ExportJob>
  get(ledgerId: string, exportId: string, signal?: AbortSignal): Promise<ExportJob>
  /** Fetches the file through the client so the bearer token reaches the server; never a bare link. */
  download(ledgerId: string, exportId: string, signal?: AbortSignal): Promise<ExportDownload>
}

const DEFAULT_FILENAME_PATTERN = /filename="?([^";]+)"?/i

export function createExportsRepository(client: TalerClient): ExportsRepository {
  const base = (ledgerId: string) => `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/exports`

  return {
    async list(ledgerId, signal) {
      const jobs = await client.call<Schemas['ExportJobResponse'][]>({
        method: 'GET',
        path: base(ledgerId),
        ...(signal === undefined ? {} : { signal }),
      })
      return jobs.map(toExportJob)
    },

    async create(ledgerId, input) {
      return toExportJob(
        await client.call<Schemas['ExportJobResponse'], Schemas['CreateExportRequest']>({
          method: 'POST',
          path: base(ledgerId),
          body: {
            format: input.format,
            fromDate: formatLocalDate(input.fromDate),
            untilDate: formatLocalDate(input.untilDate),
          },
        }),
      )
    },

    async get(ledgerId, exportId, signal) {
      return toExportJob(
        await client.call<Schemas['ExportJobResponse']>({
          method: 'GET',
          path: `${base(ledgerId)}/${encodeURIComponent(exportId)}`,
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async download(ledgerId, exportId, signal) {
      const response = await client.callRaw({
        method: 'GET',
        path: `${base(ledgerId)}/${encodeURIComponent(exportId)}/download`,
        accept: 'application/json, text/csv, application/problem+json',
        ...(signal === undefined ? {} : { signal }),
      })
      const contentType = response.headers['content-type'] ?? 'text/csv;charset=utf-8'
      const disposition = response.headers['content-disposition']
      const filename = disposition === undefined ? undefined : DEFAULT_FILENAME_PATTERN.exec(disposition)?.[1]
      const extension = contentType.includes('application/json') ? 'json' : 'csv'
      return {
        blob: new Blob([response.body], { type: contentType }),
        filename: filename ?? `export-${exportId}.${extension}`,
      }
    },
  }
}

function toExportJob(response: Schemas['ExportJobResponse']): ExportJob {
  return {
    id: response.id,
    status: response.status,
    format: response.format,
    createdAt: parseInstant(response.createdAt),
    startedAt: response.startedAt === undefined ? undefined : parseInstant(response.startedAt),
    completedAt: response.completedAt === undefined ? undefined : parseInstant(response.completedAt),
    fromDate: parseLocalDate(response.fromDate),
    untilDate: parseLocalDate(response.untilDate),
    requestedBy: response.requestedBy,
    errorMessage: response.errorMessage,
  }
}
